import { useEffect, useMemo, useState } from 'react';
import { useConversations } from './hooks/useConversations';
import { useThread } from './hooks/useThread';
import { useGroupChats } from './hooks/useGroupChats';
import { useGroupThread } from './hooks/useGroupThread';
import { useAnnouncements } from './hooks/useAnnouncements';
import { useConversationSummaries } from './hooks/useConversationSummaries';
import { ConversationList } from './components/ConversationList';
import { ThreadPanel } from './components/ThreadPanel';
import { GroupThreadPanel } from './components/GroupThreadPanel';
import { AnnouncementsView } from './components/AnnouncementsView';
import { AnnouncementComposer } from './components/AnnouncementComposer';
import { EmptyThread } from './components/EmptyThread';
import { Toast } from '../../components/common/Toast';
import type { MergedRow, Selection } from './types';

export function MessagesPage() {
  const {
    conversations, loading: convsLoading,
    searchQuery, setSearchQuery,
    filter, setFilter,
    shelveConversation, unshelveConversation, markAsRead,
  } = useConversations();
  const { groupChats, loading: groupsLoading } = useGroupChats();
  const { announcements, loading: annLoading, sending: annSending, sendAnnouncement } = useAnnouncements();

  const [selection, setSelection] = useState<Selection>(null);
  const [composeOpen, setComposeOpen] = useState(false);
  const [toast, setToast] = useState<{ msg: string; type: 'success' | 'error'; visible: boolean }>(
    { msg: '', type: 'success', visible: false },
  );

  const showToast = (msg: string, type: 'success' | 'error') => setToast({ msg, type, visible: true });

  const dmId = selection?.kind === 'dm' ? selection.id : null;
  const groupId = selection?.kind === 'group' ? selection.id : null;

  const dmThread = useThread(dmId);
  const groupThread = useGroupThread(groupId);

  // Mark a DM conversation as read when selected.
  useEffect(() => {
    if (dmId) void markAsRead(dmId);
  }, [dmId, markAsRead]);

  useEffect(() => { document.title = 'CrewBit · Messages'; }, []);

  // Merge DMs + group chats into one sorted list (mirrors mobile's merged inbox).
  const rows = useMemo<MergedRow[]>(() => {
    const dmRows: MergedRow[] = conversations.map(c => ({
      id: c.id,
      kind: 'dm',
      name: c.otherParticipantName,
      preview: c.last_message_preview,
      lastMessageAt: c.last_message_at,
      unread: c.unreadCount > 0,
      shelved: c.shelved,
      role: c.otherParticipantRole,
      memberCount: null,
    }));

    const q = searchQuery.trim().toLowerCase();
    const groupRows: MergedRow[] = groupChats
      .filter(() => filter !== 'clients')          // groups are team surfaces
      .filter(g => !q || g.name.toLowerCase().includes(q))
      .map(g => ({
        id: g.id,
        kind: 'group',
        name: g.name,
        preview: g.last_message_preview,
        lastMessageAt: g.last_message_at,
        unread: false,
        shelved: false,
        role: null,
        memberCount: g.memberCount,
      }));

    return [...dmRows, ...groupRows].sort((a, b) => {
      if (a.shelved && !b.shelved) return 1;
      if (!a.shelved && b.shelved) return -1;
      const at = a.lastMessageAt ? new Date(a.lastMessageAt).getTime() : 0;
      const bt = b.lastMessageAt ? new Date(b.lastMessageAt).getTime() : 0;
      return bt - at;
    });
  }, [conversations, groupChats, filter, searchQuery]);

  // §T.157 status line — refetch when the row set changes.
  const { summaries } = useConversationSummaries(rows);

  const activeConversation = useMemo(
    () => conversations.find(c => c.id === dmId) ?? null,
    [conversations, dmId],
  );
  const activeGroup = useMemo(
    () => groupChats.find(g => g.id === groupId) ?? null,
    [groupChats, groupId],
  );

  const handleSelectRow = (kind: 'dm' | 'group', id: string) => setSelection({ kind, id });
  const openAnnouncements = () => setSelection({ kind: 'announcements' });

  return (
    <div style={{
      display: 'flex',
      height: 'calc(100vh - 56px)',
      margin: '-24px',
      overflow: 'hidden',
    }}>
      <ConversationList
        rows={rows}
        summaries={summaries}
        loading={convsLoading || groupsLoading}
        searchQuery={searchQuery}
        onSearchChange={setSearchQuery}
        filter={filter}
        onFilterChange={setFilter}
        selection={selection}
        onSelectRow={handleSelectRow}
        onOpenAnnouncements={openAnnouncements}
        announcementPreview={announcements[0]?.content ?? null}
      />

      {selection?.kind === 'dm' && activeConversation ? (
        <ThreadPanel
          conversation={activeConversation}
          messages={dmThread.messages}
          loading={dmThread.loading}
          hasMore={dmThread.hasMore}
          onLoadMore={dmThread.loadMore}
          onSend={dmThread.sendMessage}
          sending={dmThread.sending}
          showTranslations={dmThread.showTranslations}
          onToggleTranslations={dmThread.toggleTranslations}
          onShelve={() => shelveConversation(activeConversation.id)}
          onUnshelve={() => unshelveConversation(activeConversation.id)}
        />
      ) : selection?.kind === 'group' && activeGroup ? (
        <GroupThreadPanel
          group={activeGroup}
          messages={groupThread.messages}
          loading={groupThread.loading}
          hasMore={groupThread.hasMore}
          onLoadMore={groupThread.loadMore}
          onSend={(c) => { groupThread.sendMessage(c).catch(() => showToast('Failed to send message', 'error')); }}
          sending={groupThread.sending}
          showTranslations={dmThread.showTranslations}
          onToggleTranslations={dmThread.toggleTranslations}
        />
      ) : selection?.kind === 'announcements' ? (
        <AnnouncementsView
          announcements={announcements}
          loading={annLoading}
          onCompose={() => setComposeOpen(true)}
        />
      ) : (
        <EmptyThread />
      )}

      <AnnouncementComposer
        isOpen={composeOpen}
        onClose={() => setComposeOpen(false)}
        onSend={sendAnnouncement}
        sending={annSending}
        onResult={showToast}
      />

      <Toast
        message={toast.msg}
        type={toast.type}
        isVisible={toast.visible}
        onDismiss={() => setToast(t => ({ ...t, visible: false }))}
      />
    </div>
  );
}
